export function detectMimeFromBytes(byteArray, fallback = 'audio/mpeg') {
  if (!byteArray || byteArray.length < 4) return fallback;
  const header4 = Array.from(byteArray.slice(0, 4))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  const header12 = Array.from(byteArray.slice(0, 12))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  // MP3 (ID3 tag or MPEG frame sync 0xFFEx)
  if (header4.startsWith('494433')) return 'audio/mpeg';
  if (header4.startsWith('fff')) return 'audio/mpeg';

  // OGG (Opus/Vorbis)
  if (header4 === '4f676753') return 'audio/ogg';

  // WAV (RIFF)
  if (header4 === '52494646') return 'audio/wav';

  // WebM / Matroska (EBML)
  if (header4 === '1a45dfa3') return 'audio/webm';

  // MP4/M4A (ftyp)
  if (header12.includes('66747970')) return 'audio/mp4';

  return fallback;
}

export function base64ToBlob(base64, mime) {
  if (!base64) throw new Error('No base64 data provided');

  // Sanitize input
  let clean = base64.trim()
    .replace(/^data:([^;]+);base64,/, '') // remove prefix if present
    .replace(/[\r\n\s]/g, '') // remove whitespace
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  // Auto-detect MIME if not given
  if (!mime) {
    if (clean.startsWith('SUQz')) mime = 'audio/mpeg';
    else if (clean.startsWith('UklG')) mime = 'audio/wav';
    else if (clean.startsWith('T2dn')) mime = 'audio/ogg';
    else mime = 'audio/mpeg';
  }

  try {
    const byteChars = atob(clean);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
    return new Blob([byteArray], { type: mime });
  } catch (e) {
    console.error('[Audio] base64 decode failed:', e);
    throw e;
  }
}

function bytesFromBase64(base64) {
  if (!base64) return null;
  const clean = base64.trim()
    .replace(/^data:([^;]+);base64,/, '')
    .replace(/[\r\n\s]/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function isKnownContainer(bytes) {
  if (!bytes || bytes.length < 4) return false;
  const b0 = bytes[0], b1 = bytes[1], b2 = bytes[2], b3 = bytes[3];
  if (b0 === 0x52 && b1 === 0x49 && b2 === 0x46 && b3 === 0x46) return true; // RIFF (WAV)
  if (b0 === 0x4F && b1 === 0x67 && b2 === 0x67 && b3 === 0x53) return true; // OggS
  if (b0 === 0x49 && b1 === 0x44 && b2 === 0x33) return true; // ID3
  if (b0 === 0xFF && (b1 & 0xE0) === 0xE0) return true; // MPEG frame
  if (b0 === 0x1A && b1 === 0x45 && b2 === 0xDF && b3 === 0xA3) return true; // EBML
  return false;
}

export function wrapPcm16ToWav(pcmBytes, sampleRate = 16000, numChannels = 1) {
  if (!pcmBytes || pcmBytes.length === 0) return null;
  const byteRate = sampleRate * numChannels * 2;
  const blockAlign = numChannels * 2;
  const dataSize = pcmBytes.length;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');
  // fmt chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // PCM
  view.setUint16(20, 1, true); // format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true); // bits per sample
  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);
  // PCM data
  new Uint8Array(buffer, 44).set(pcmBytes);
  return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view, offset, str) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

// Convert Float32 PCM samples (-1.0..1.0) to 16-bit PCM little-endian bytes
export function float32ToPCM16Bytes(float32Array) {
  if (!float32Array || float32Array.length === 0) return new Uint8Array(0);
  const out = new Uint8Array(float32Array.length * 2);
  let offset = 0;
  for (let i = 0; i < float32Array.length; i++) {
    // clamp and convert
    let s = Math.max(-1, Math.min(1, float32Array[i] || 0));
    // Scale to int16 range
    const val = s < 0 ? s * 0x8000 : s * 0x7FFF;
    const int16 = val | 0;
    out[offset++] = int16 & 0xFF;
    out[offset++] = (int16 >> 8) & 0xFF;
  }
  return out;
}

// Sample MP3 base64 for fallback testing
// Known-good tiny WAV beep (22050Hz mono PCM ~0.1s)
// This avoids MP3 demuxer variability when testing the pipeline
export const SAMPLE_MP3_BASE64 =
  "UklGRmQAAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YQwAAAABAQEBAQEBAQEBAQEBAQEBAQEBAP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AAAAAAD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AAAAAA==";


export async function playAudioFromBase64(base64, id, mime = 'audio/mpeg', refs = {}) {
  const { ensureExclusive, setGlobalAudio } = await import('./audioManager.js');
  // Extract MIME if present and sanitize base64
  let extractedMime = null;
  let realBase64Mp3 = (base64 || '').trim();
  const prefixMatch = realBase64Mp3.match(/^data:([^;]+);base64,/);
  if (prefixMatch) {
    extractedMime = prefixMatch[1];
    realBase64Mp3 = realBase64Mp3.replace(/^data:([^;]+);base64,/, '');
  }
  realBase64Mp3 = realBase64Mp3
    .replace(/[\r\n\s]/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const SAMPLE = (SAMPLE_MP3_BASE64 || '').trim();

  const { audioRef, base64ObjectUrlRef, setPlayingAudioId, setIsPlaying } = refs;
  if (!base64 || base64.length < 100) {
    console.error('[Audio] Base64 data too short:', base64?.length || 0); 
    return;
  }

  let audioEl = audioRef?.current;
  if (!audioEl) {
    audioEl = new Audio();
    if (audioRef) audioRef.current = audioEl;
  }
  audioEl.preload = 'auto';
  // Enforce single playback globally
  try { ensureExclusive(audioEl); setGlobalAudio(audioEl); } catch {}

  // Revoke previous object URL if any
  if (base64ObjectUrlRef?.current) {
    try { URL.revokeObjectURL(base64ObjectUrlRef.current); } catch {}
    base64ObjectUrlRef.current = null;
  }

  // Try playing via data URI first (more compatible for some MP3 decoders)
  let blobUrl = null;
  let usingSample = false;

  // Detect MIME robustly
  let resolvedMime = extractedMime || mime || 'audio/mpeg';
  try {
    // Peek first bytes to refine MIME
    const peekChars = atob(realBase64Mp3.slice(0, 64));
    const peekArray = new Uint8Array(peekChars.length);
    for (let i = 0; i < peekChars.length; i++) peekArray[i] = peekChars.charCodeAt(i);
    resolvedMime = detectMimeFromBytes(peekArray, resolvedMime);
  } catch {}

  const dataUriPrimary = `data:${resolvedMime};base64,${realBase64Mp3}`;
  const sampleMime = (() => {
    try {
      const peekSample = atob(SAMPLE.slice(0, 64));
      const arr = new Uint8Array(peekSample.length);
      for (let i = 0; i < peekSample.length; i++) arr[i] = peekSample.charCodeAt(i);
      return detectMimeFromBytes(arr, 'audio/wav');
    } catch {
      return 'audio/wav';
    }
  })();
  const dataUriSample = `data:${sampleMime};base64,${SAMPLE}`;
  audioEl.src = dataUriPrimary;
  audioEl.currentTime = 0;

  // Ensure we wait for readiness before play
  const waitForCanPlay = () => new Promise((resolve, reject) => {
    const onReady = () => {
      cleanupReady();
      resolve();
    };
    const onError = (e) => {
      cleanupReady();
      reject(e);
    };
    const cleanupReady = () => {
      audioEl.removeEventListener('canplaythrough', onReady);
      audioEl.removeEventListener('error', onError);
    };
    audioEl.addEventListener('canplaythrough', onReady, { once: true });
    audioEl.addEventListener('error', onError, { once: true });
    try { audioEl.load(); } catch {}
  });

  // Handlers
  let fallbackTriggered = false;
  const cleanupUrl = () => {
    try { URL.revokeObjectURL(blobUrl); } catch {}
    if (base64ObjectUrlRef) base64ObjectUrlRef.current = null;
  };
  audioEl.onended = () => {
    setIsPlaying?.(false);
    setPlayingAudioId?.(null);
    cleanupUrl();
  };
  audioEl.onerror = async (e) => {
    console.error('[Audio] element error:', audioEl?.error?.code, audioEl?.error?.message || '', e);
    setIsPlaying?.(false);
    setPlayingAudioId?.(null);
    cleanupUrl();
    if (!fallbackTriggered && audioContext) {
      fallbackTriggered = true;
      try {
        await playAudioFromBase64AudioContext(realBase64Mp3, id, { setPlayingAudioId, setIsPlaying });
      } catch (err) {
        console.warn('[Audio] AudioContext fallback decode failed');
      }
    }
  };

  // Try a matrix of MIME candidates for data URI and Blob approaches
  const candidateMimes = Array.from(new Set([
    resolvedMime,
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/ogg',
  ]));

  const tryPlaySrc = async (srcSetter) => {
    try {
      srcSetter();
      audioEl.currentTime = 0;
      await waitForCanPlay();
      try { ensureExclusive(audioEl); setGlobalAudio(audioEl); } catch {}
      await audioEl.play();
      setPlayingAudioId?.(id);
      setIsPlaying?.(true);
      return true;
    } catch (e) {
      return false;
    }
  };

  // 1) Primary via data URI
  if (await tryPlaySrc(() => { audioEl.src = dataUriPrimary; })) return;

  // 2) Sample via data URI
  if (await tryPlaySrc(() => { audioEl.src = dataUriSample; usingSample = true; })) return;

  // 3) Try candidate mimes via data URI (primary)
  for (const cm of candidateMimes) {
    const uri = `data:${cm};base64,${realBase64Mp3}`;
    if (await tryPlaySrc(() => { audioEl.src = uri; })) return;
  }

  // 4) Try candidate mimes via data URI (sample)
  for (const cm of candidateMimes) {
    const uri = `data:${cm};base64,${SAMPLE}`;
    if (await tryPlaySrc(() => { audioEl.src = uri; usingSample = true; })) return;
  }

  // 5) Primary Blob/ObjectURL (resolved)
  try {
    const blob = base64ToBlob(realBase64Mp3, resolvedMime);
    blobUrl = URL.createObjectURL(blob);
    if (base64ObjectUrlRef) base64ObjectUrlRef.current = blobUrl;
    if (await tryPlaySrc(() => { audioEl.src = blobUrl; })) return;
  } catch (blobErrPrimary) {
    console.warn('[Audio] Blob playback failed (primary resolved):', blobErrPrimary);
  }

  // 6) Sample Blob/ObjectURL (sampleMime)
  try {
    const blobSample = base64ToBlob(SAMPLE, sampleMime);
    blobUrl = URL.createObjectURL(blobSample);
    if (base64ObjectUrlRef) base64ObjectUrlRef.current = blobUrl;
    if (await tryPlaySrc(() => { audioEl.src = blobUrl; usingSample = true; })) return;
  } catch (blobErrSample) {
    console.warn('[Audio] sample Blob playback failed:', blobErrSample);
  }

  // 7) Primary Blob/ObjectURL with candidate mimes
  for (const cm of candidateMimes) {
    try {
      const blob = base64ToBlob(realBase64Mp3, cm);
      blobUrl = URL.createObjectURL(blob);
      if (base64ObjectUrlRef) base64ObjectUrlRef.current = blobUrl;
      const ok = await tryPlaySrc(() => { audioEl.src = blobUrl; });
      if (ok) return;
    } catch {}
  }

  // 8) Sample Blob/ObjectURL with candidate mimes
  for (const cm of candidateMimes) {
    try {
      const blob = base64ToBlob(SAMPLE, cm);
      blobUrl = URL.createObjectURL(blob);
      if (base64ObjectUrlRef) base64ObjectUrlRef.current = blobUrl;
      const ok = await tryPlaySrc(() => { audioEl.src = blobUrl; usingSample = true; });
      if (ok) return;
    } catch {}
  }

  // Cleanup object URL if created
  if (blobUrl) {
    try { URL.revokeObjectURL(blobUrl); } catch {}
    if (base64ObjectUrlRef) base64ObjectUrlRef.current = null;
  }

  // 9) Fallback: try AudioContext decode of full clip
  if (!fallbackTriggered && audioContext) {
    fallbackTriggered = true;
    try {
      await playAudioFromBase64AudioContext(realBase64Mp3, id, { setPlayingAudioId, setIsPlaying });
      return;
    } catch (errCtxPrimary) {
      console.warn('[Audio] AudioContext fallback decode failed (primary)');
    }

    try {
      await playAudioFromBase64AudioContext(SAMPLE, id, { setPlayingAudioId, setIsPlaying });
      usingSample = true;
      return;
    } catch (errCtxSample) {
      console.error('[Audio] AudioContext sample decode failed:', errCtxSample);
    }

    // 10) Last resort: wrap as WAV if bytes look like raw PCM
    try {
      const primaryBytes = bytesFromBase64(realBase64Mp3);
      if (primaryBytes && !isKnownContainer(primaryBytes)) {
        const wavBlob = wrapPcm16ToWav(primaryBytes, 16000, 1);
        const wavUrl = URL.createObjectURL(wavBlob);
        const ok = await (async () => {
          try {
            audioEl.src = wavUrl;
            audioEl.currentTime = 0;
            await waitForCanPlay();
            await audioEl.play();
            setPlayingAudioId?.(id);
            setIsPlaying?.(true);
            return true;
          } catch {
            return false;
          } finally {
            try { URL.revokeObjectURL(wavUrl); } catch {}
          }
        })();
        if (ok) return;
        // Try decode via AudioContext
        try {
          const reader = new FileReader();
          const arrayBuf = await new Promise((res, rej) => {
            reader.onload = () => res(reader.result);
            reader.onerror = rej;
            reader.readAsArrayBuffer(wavBlob);
          });
          const decoded = await audioContext.decodeAudioData(arrayBuf);
          const source = audioContext.createBufferSource();
          source.buffer = decoded;
          source.connect(audioContext.destination);
          source.start(0);
          return;
        } catch {}
      }
    } catch {}
  }
}

export const audioContext = typeof window !== 'undefined'
  ? new (window.AudioContext || window.webkitAudioContext)()
  : null;

// Expose on window for cross-module access and allow resuming on gesture
if (typeof window !== 'undefined') {
  try { window._globalAudioCtx = audioContext; } catch {}
}

export async function resumeAudioContextIfNeeded() {
  try { await audioContext?.resume?.(); } catch {}
}

// Track active AudioContext source for global stop/pause
let activeAudioSource = null;
export function setActiveAudioContextSource(source) {
  activeAudioSource = source || null;
}
export function stopActiveAudioContextSource() {
  try {
    if (activeAudioSource) {
      activeAudioSource.stop(0);
    }
  } catch {}
  activeAudioSource = null;
}

export async function playAudioFromBase64AudioContext(base64, id = null, refs = {}) {
  if (!base64 || base64.length < 100) {
    console.warn("[Audio] Skipped: Base64 too short");
    return;
  }

  if (typeof AudioContext === "undefined" && typeof webkitAudioContext === "undefined") {
    console.error("[Audio] AudioContext not supported in this environment");
    return;
  }

  const audioContext = window.audioContext || new (window.AudioContext || window.webkitAudioContext)();
  window.audioContext = audioContext;

  try {
    // Sanitize & convert to bytes
    base64 = base64.replace(/^data:audio\/\w+;base64,/, "").trim();
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    // Detect format by magic bytes
    const header = bytes.slice(0, 4);
    let mime = "audio/mpeg";
    if (header[0] === 0x52 && header[1] === 0x49) mime = "audio/wav";  // 'RIFF'
    else if (header[0] === 0x66 && header[1] === 0x4C) mime = "audio/flac"; // 'fL'
    else if (header[0] === 0x4F && header[1] === 0x67) mime = "audio/ogg";  // 'OggS'
    else if (header[0] === 0x49 && header[1] === 0x44) mime = "audio/mpeg"; // 'ID3' (MP3 tag)
    else if (header[0] === 0xFF && (header[1] & 0xE0) === 0xE0) mime = "audio/mpeg"; // MP3 frame

    try {
      await audioContext.resume();
    } catch {}

    // Attempt to decode directly
    try {
      const decoded = await audioContext.decodeAudioData(bytes.buffer.slice(0));
      const source = audioContext.createBufferSource();
      source.buffer = decoded;
      source.connect(audioContext.destination);
      setActiveAudioContextSource(source);
      const { setPlayingAudioId, setIsPlaying } = refs || {};
      try { setPlayingAudioId?.(id ?? null); setIsPlaying?.(true); } catch {}
      source.onended = () => {
        try { setIsPlaying?.(false); setPlayingAudioId?.(null); } catch {}
        setActiveAudioContextSource(null);
      };
      source.start(0);
      console.log(`[Audio] Played via AudioContext (${mime})`);
      return;
    } catch (decodeErr) {
      console.warn("[Audio] decodeAudioData failed:", decodeErr);
    }

    // Try wrapping as WAV if bytes look like raw PCM
    try {
      if (!isKnownContainer(bytes)) {
        const wavBlob = wrapPcm16ToWav(bytes, 16000, 1);
        const arrayBuf = await wavBlob.arrayBuffer();
        const decoded = await audioContext.decodeAudioData(arrayBuf);
        const source = audioContext.createBufferSource();
        source.buffer = decoded;
        source.connect(audioContext.destination);
        setActiveAudioContextSource(source);
        const { setPlayingAudioId, setIsPlaying } = refs || {};
        try { setPlayingAudioId?.(id ?? null); setIsPlaying?.(true); } catch {}
        source.onended = () => {
          try { setIsPlaying?.(false); setPlayingAudioId?.(null); } catch {}
          setActiveAudioContextSource(null);
        };
        source.start(0);
        console.log('[Audio] Played via AudioContext (wrapped WAV)');
        return;
      }
    } catch (wrapErr) {
      console.warn('[Audio] WAV wrap+decode failed:', wrapErr);
    }

    // Fallback to HTMLAudio element
    console.warn("[Audio] decodeAudioData failed — falling back to <audio> playback");
    const blob = new Blob([bytes], { type: mime });
    const blobUrl = URL.createObjectURL(blob);
    const audioEl = new Audio(blobUrl);
    audioEl.onended = () => {
      try { URL.revokeObjectURL(blobUrl); } catch {}
    };
    await audioEl.play();

  } catch (err) {
    console.error("[Audio] Playback failed:", err);
  }
}
